import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import CannyChangelogButton from 'components/CannyChangelogButton';
import Logo from 'components/Logo';
import { usePostOnboardingAccountSetupContext } from 'features/PostOnboardingAccountSetup/context';
import SetupCompletedModal from 'features/PostOnboardingAccountSetup/pages/SetupCompletedModal';
import * as referralRoutes from 'features/Referral/routes';
import { ALL_USER_MENU_ITEMS } from 'features/Sidebar/UserMenu/types';
import { useIsBrowsingDisabled } from 'layouts/AppLayout/context';

import { AccountSetupBox } from './AccountSetupBox';
import { useSidebarData } from './hooks/useSidebarData';
import { Navigation } from './Navigation';
import ReferralBox from './ReferralBox';
import UserMenu from './UserMenu';

const HEADER_HEIGHT = 64;

export function Sidebar(): ReactNode {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const isBrowsingDisabled = useIsBrowsingDisabled();
  const location = useLocation();

  const isOnReferralRoute = location.pathname.includes(referralRoutes.root);

  const { loading, remainingReferrerAmount } = useSidebarData();

  const { hasCompletedPostOnboardingSetup } =
    usePostOnboardingAccountSetupContext();

  const shouldShowAccountSetupBox =
    canAccessFeature({ name: FeatureName.Activationonboarding }) &&
    !hasCompletedPostOnboardingSetup;

  const shouldShowReferralBox =
    !isOnReferralRoute &&
    !loading &&
    remainingReferrerAmount &&
    canAccessFeature({ name: FeatureName.Referral });

  return (
    <Box
      as="aside"
      backgroundColor="grey.100"
      display="flex"
      flexDirection="column"
      gridArea="sidebar"
      height="100vh"
      minHeight="100%"
      overflow="visible"
      position="relative"
    >
      <Flex
        backdropFilter="drop-shadow(4px 4px 10px blue) blur(12px)"
        backgroundColor="rgba(252, 251, 248, 0.40)"
        height={HEADER_HEIGHT}
        left={0}
        padding="space-16"
        position="absolute"
        right={0}
        zIndex={2}
      >
        <UserMenu
          disableBrowsingFor={
            isBrowsingDisabled ? ALL_USER_MENU_ITEMS : new Set([])
          }
        />
      </Flex>

      <Flex
        flexDirection="column"
        height="100%"
        overflowY="auto"
        paddingTop={HEADER_HEIGHT + 16}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Navigation />
        <Flex
          flexDirection="column"
          flexGrow="1"
          justifyContent="flex-end"
          marginTop="space-24"
        >
          {shouldShowAccountSetupBox ? <AccountSetupBox /> : null}

          {shouldShowReferralBox && !shouldShowAccountSetupBox ? (
            <ReferralBox
              remainingReferrerAmount={remainingReferrerAmount ?? null}
            />
          ) : null}

          <Flex
            flexDirection="row"
            justifyContent="space-between"
            paddingX="space-16"
            paddingY="space-12"
          >
            <Logo width="53px" />

            {canAccessFeature({ name: FeatureName.Canny }) ? (
              <CannyChangelogButton />
            ) : null}
          </Flex>
        </Flex>
      </Flex>
      <SetupCompletedModal />
    </Box>
  );
}
